import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  KPIGrid, DataTable, Card, Btn, Alert, Tabs,
  FilterPills, StatusBadge, StandardModal,
  colors, inputStyle, selectStyle, textareaStyle,
} from "../design-system";

const API = "/api";
const token = () => localStorage.getItem("token");
const authHeaders = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${token()}` });

const STATUS_COLORS = {
  draft: "default", submitted: "warning", under_review: "warning",
  conditional: "warning", approved: "success", rejected: "danger",
  active: "success", suspended: "warning", lapsed: "danger",
  revoked: "danger", expired: "danger",
};

const GRADE_COLORS = {
  affiliate: "#6366f1", professional: "#0ea5e9", senior: "#f59e0b", fellow: "#8b5cf6",
};

function GradePill({ grade, postNominal }) {
  const color = GRADE_COLORS[grade] || colors.textMuted;
  return (
    <span style={{
      background: color + "22", color, border: `1px solid ${color}55`,
      borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 600,
    }}>
      {postNominal || grade}
    </span>
  );
}

const TABS = [
  { key: "overview",     label: "Overview" },
  { key: "applications", label: "Applications" },
  { key: "members",      label: "Active Members" },
  { key: "billing",      label: "Billing" },
];

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "submitted", label: "Submitted" },
  { value: "under_review", label: "Under Review" },
  { value: "conditional", label: "Conditional" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "lapsed", label: "Lapsed" },
  { value: "revoked", label: "Revoked" },
];

const INVOICE_STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "overdue", label: "Overdue" },
  { value: "paid", label: "Paid" },
  { value: "cancelled", label: "Cancelled" },
];

export default function MembershipDashboard() {
  const navigate = useNavigate();
  const [tab, setTab]               = useState("overview");
  const [dashboard, setDashboard]   = useState(null);
  const [applications, setApps]     = useState([]);
  const [invoices, setInvoices]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [statusFilter, setStatus]   = useState("");
  const [invFilter, setInvFilter]   = useState("");
  const [msg, setMsg]               = useState(null);
  const [msgType, setMsgType]       = useState("success");
  const [newAppModal, setNewApp]    = useState(false);
  const [grades, setGrades]         = useState([]);

  // New application form state
  const [form, setForm] = useState({
    applicant_name: "", applicant_email: "", applicant_phone: "",
    job_title: "", organisation: "", grade_id: "",
    billing_years: "1", discount_provider: false, discount_trainer: false,
    personal_statement: "", coi_declared: false, coi_details: "",
  });
  const [feeCalc, setFeeCalc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [payModal, setPayModal] = useState(null);
  const [payForm, setPayForm] = useState({ paid_amount: "", payment_reference: "", confirm_mismatch: false });
  const [paySaving, setPaySaving] = useState(false);

  const flash = (m, t = "success") => { setMsg(m); setMsgType(t); setTimeout(() => setMsg(null), 4000); };

  const loadDashboard = useCallback(async () => {
    try {
      const [dRes, aRes, iRes, gRes] = await Promise.all([
        fetch(`${API}/memberships/dashboard`,    { headers: authHeaders() }),
        fetch(`${API}/memberships/applications`, { headers: authHeaders() }),
        fetch(`${API}/memberships/invoices`,     { headers: authHeaders() }),
        fetch(`${API}/memberships/grades`,       { headers: authHeaders() }),
      ]);
      if (dRes.ok) setDashboard(await dRes.json());
      if (aRes.ok) setApps(await aRes.json());
      if (iRes.ok) setInvoices(await iRes.json());
      if (gRes.ok) setGrades(await gRes.json());
    } catch (e) {
      flash("Failed to load dashboard", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  // Auto-calculate fee when grade/billing/discounts change
  useEffect(() => {
    if (!form.grade_id) return;
    fetch(
      `${API}/memberships/calculate-fee?grade_id=${form.grade_id}&billing_years=${form.billing_years}&discount_provider=${form.discount_provider}&discount_trainer=${form.discount_trainer}`,
      { headers: authHeaders() }
    ).then(r => r.ok ? r.json() : null).then(d => setFeeCalc(d));
  }, [form.grade_id, form.billing_years, form.discount_provider, form.discount_trainer]);

  async function handleCreateApp(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/memberships/applications`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ ...form, grade_id: parseInt(form.grade_id), billing_years: parseInt(form.billing_years) }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      const data = await res.json();
      flash("Application created successfully");
      setNewApp(false);
      navigate(`/membership/applications/${data.id}`);
    } catch (e) {
      flash(e.message, "error");
    } finally {
      setSaving(false);
    }
  }

  async function submitMarkPaid(e) {
    e.preventDefault();
    setPaySaving(true);
    try {
      const res = await fetch(`${API}/memberships/invoices/${payModal.id}/mark-paid`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          paid_amount: parseFloat(payForm.paid_amount),
          payment_reference: payForm.payment_reference,
          confirm_mismatch: payForm.confirm_mismatch,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to update invoice");
      flash("Invoice marked as paid");
      setPayModal(null);
      loadDashboard();
    } catch (err) {
      const mismatch = /does not match/i.test(err.message);
      if (mismatch) {
        setPayForm(f => ({ ...f, confirm_mismatch: true }));
        flash(`${err.message} Click "Confirm Payment" again to record it anyway.`, "error");
      } else {
        flash(err.message, "error");
      }
    } finally {
      setPaySaving(false);
    }
  }

  const kpis = dashboard ? [
    { label: "Active Members",    value: dashboard.kpis.active_members,   color: "green",  icon: "👥" },
    { label: "Pending Applications", value: dashboard.kpis.pending_apps,  color: "amber",  icon: "📋" },
    { label: "Expiring (90 days)", value: dashboard.kpis.expiring_in_90d, color: "orange", icon: "⏳" },
    { label: "Overdue Invoices",  value: dashboard.kpis.overdue_invoices, color: "red",    icon: "⚠️" },
    { label: "Revenue YTD",       value: `£${(dashboard.kpis.revenue_ytd || 0).toLocaleString()}`, color: "blue", icon: "💷" },
  ] : [];

  const filteredApps = applications.filter(a => !statusFilter || a.status === statusFilter);
  const activeMembers = applications.filter(a => a.status === "active");
  const filteredInvoices = invoices.filter(i => !invFilter || i.status === invFilter);

  const appColumns = [
    { key: "applicant_name", label: "Applicant", render: (_, r) => (
      <div>
        <div style={{ fontWeight: 600 }}>{r.applicant_name}</div>
        <div style={{ fontSize: 12, color: colors.textMuted }}>{r.applicant_email}</div>
      </div>
    )},
    { key: "grade_name", label: "Grade", render: (_, r) => <GradePill grade={r.grade_code} postNominal={r.post_nominal} /> },
    { key: "status", label: "Status", render: (_, r) => <StatusBadge status={r.status} /> },
    { key: "application_date", label: "Applied", render: (_, r) => r.application_date ? new Date(r.application_date).toLocaleDateString("en-GB") : "—" },
    { key: "final_fee", label: "Fee", render: (_, r) => r.final_fee ? `£${r.final_fee.toFixed(2)}` : "—" },
    { key: "actions", label: "", render: (_, r) => (
      <Btn size="sm" variant="ghost" onClick={() => navigate(`/membership/applications/${r.id}`)}>View</Btn>
    )},
  ];

  const memberColumns = [
    { key: "applicant_name", label: "Member", render: (_, r) => (
      <div>
        <div style={{ fontWeight: 600 }}>{r.applicant_name}</div>
        <div style={{ fontSize: 12, color: colors.textMuted }}>{r.organisation || r.applicant_email}</div>
      </div>
    )},
    { key: "membership_number", label: "Number", render: (_, r) => (
      <span style={{ fontFamily: "monospace", fontWeight: 600, color: colors.primary }}>{r.membership_number}</span>
    )},
    { key: "grade_name", label: "Grade", render: (_, r) => <GradePill grade={r.grade_code} postNominal={r.post_nominal} /> },
    { key: "valid_until", label: "Expires", render: (_, r) => {
      if (!r.valid_until) return "—";
      const d = new Date(r.valid_until);
      const soon = (d - Date.now()) < 90 * 86400000;
      return <span style={{ color: soon ? colors.warning : "inherit" }}>{d.toLocaleDateString("en-GB")}</span>;
    }},
    { key: "status", label: "Status", render: (_, r) => <StatusBadge status={r.status} /> },
    { key: "actions", label: "", render: (_, r) => (
      <div style={{ display: "flex", gap: 6 }}>
        <Btn size="sm" variant="ghost" onClick={() => navigate(`/membership/applications/${r.id}`)}>View</Btn>
        <Btn size="sm" variant="ghost" onClick={() => navigate(`/membership/applications/${r.id}/decision`)}>Decisions</Btn>
      </div>
    )},
  ];

  const invoiceColumns = [
    { key: "invoice_number", label: "Invoice", render: (_, r) => (
      <span style={{ fontFamily: "monospace", fontSize: 13 }}>{r.invoice_number}</span>
    )},
    { key: "membership", label: "Member", render: (_, r) => {
      const m = applications.find(a => a.id === r.membership_id);
      return m ? <span>{m.applicant_name}</span> : <span style={{ color: colors.textMuted }}>—</span>;
    }},
    { key: "invoice_type", label: "Type", render: (_, r) => r.invoice_type.replace("_", " ").replace(/\b\w/g, c => c.toUpperCase()) },
    { key: "amount", label: "Amount", render: (_, r) => `£${r.amount.toFixed(2)}` },
    { key: "status", label: "Status", render: (_, r) => <StatusBadge status={r.status} /> },
    { key: "due_date", label: "Due", render: (_, r) => {
      const d = new Date(r.due_date);
      const overdue = r.status === "pending" && d < new Date();
      return <span style={{ color: overdue ? colors.danger : "inherit" }}>{d.toLocaleDateString("en-GB")}</span>;
    }},
    { key: "actions", label: "", render: (_, r) => r.status === "pending" || r.status === "overdue" ? (
      <Btn size="sm" variant="success" onClick={() => {
        setPayForm({ paid_amount: r.amount.toFixed(2), payment_reference: "", confirm_mismatch: false });
        setPayModal(r);
      }}>Mark Paid</Btn>
    ) : null },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: colors.textPrimary, margin: 0 }}>Membership</h1>
          <p style={{ color: colors.textMuted, margin: "4px 0 0" }}>ICSC professional membership management</p>
        </div>
        <Btn variant="primary" onClick={() => setNewApp(true)}>+ New Application</Btn>
      </div>

      {msg && <Alert type={msgType} message={msg} style={{ marginBottom: 16 }} />}

      <KPIGrid kpis={kpis} columns={5} />

      <Tabs
        tabs={TABS}
        activeTab={tab}
        onChange={setTab}
        style={{ marginBottom: 24 }}
      />

      {/* ---- OVERVIEW ---- */}
      {tab === "overview" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          <Card padding={24}>
            <h3 style={{ fontWeight: 600, marginBottom: 16, fontSize: 15 }}>Expiring Soon (30 days)</h3>
            {!dashboard?.expiring_soon?.length ? (
              <p style={{ color: colors.textMuted, fontSize: 14 }}>No memberships expiring in the next 30 days.</p>
            ) : dashboard.expiring_soon.map(m => (
              <div key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${colors.border}` }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{m.applicant_name}</div>
                  <div style={{ fontSize: 12, color: colors.textMuted }}>{m.membership_number} · {m.grade_name}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 13, color: colors.warning, fontWeight: 600 }}>{new Date(m.valid_until).toLocaleDateString("en-GB")}</div>
                  <Btn size="sm" variant="ghost" onClick={() => navigate(`/membership/applications/${m.id}`)}>View</Btn>
                </div>
              </div>
            ))}
          </Card>

          <Card padding={24}>
            <h3 style={{ fontWeight: 600, marginBottom: 16, fontSize: 15 }}>Pending Applications</h3>
            {!dashboard?.pending_apps?.length ? (
              <p style={{ color: colors.textMuted, fontSize: 14 }}>No pending applications.</p>
            ) : dashboard.pending_apps.map(m => (
              <div key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${colors.border}` }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{m.applicant_name}</div>
                  <div style={{ fontSize: 12, color: colors.textMuted }}>{m.organisation || m.applicant_email}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <StatusBadge status={m.status} size="sm" />
                  <div style={{ marginTop: 4 }}>
                    <Btn size="sm" variant="ghost" onClick={() => navigate(`/membership/applications/${m.id}`)}>Review</Btn>
                  </div>
                </div>
              </div>
            ))}
          </Card>
        </div>
      )}

      {/* ---- APPLICATIONS ---- */}
      {tab === "applications" && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <FilterPills options={STATUS_FILTER_OPTIONS} value={statusFilter} onChange={setStatus} />
          </div>
          <DataTable columns={appColumns} rows={filteredApps} keyField="id" loading={loading} emptyMessage="No applications found." />
        </div>
      )}

      {/* ---- MEMBERS ---- */}
      {tab === "members" && (
        <DataTable columns={memberColumns} rows={activeMembers} keyField="id" loading={loading} emptyMessage="No active members." />
      )}

      {/* ---- BILLING ---- */}
      {tab === "billing" && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <FilterPills options={INVOICE_STATUS_OPTIONS} value={invFilter} onChange={setInvFilter} />
          </div>
          <DataTable columns={invoiceColumns} rows={filteredInvoices} keyField="id" loading={loading} emptyMessage="No invoices found." />
        </div>
      )}

      {/* ---- NEW APPLICATION MODAL ---- */}
      {newAppModal && (
        <StandardModal
          title="New Membership Application"
          onClose={() => setNewApp(false)}
          width={680}
          footer={
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => setNewApp(false)}>Cancel</Btn>
              <Btn variant="primary" type="submit" form="new-app-form" disabled={saving}>
                {saving ? "Creating…" : "Create Application"}
              </Btn>
            </div>
          }
        >
          <form id="new-app-form" onSubmit={handleCreateApp}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Full Name *</label>
                <input required value={form.applicant_name} onChange={e => setForm(f => ({ ...f, applicant_name: e.target.value }))}
                  style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Email *</label>
                <input required type="email" value={form.applicant_email} onChange={e => setForm(f => ({ ...f, applicant_email: e.target.value }))}
                  style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Job Title</label>
                <input value={form.job_title} onChange={e => setForm(f => ({ ...f, job_title: e.target.value }))}
                  style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Organisation</label>
                <input value={form.organisation} onChange={e => setForm(f => ({ ...f, organisation: e.target.value }))}
                  style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Grade *</label>
                <select required value={form.grade_id} onChange={e => setForm(f => ({ ...f, grade_id: e.target.value }))}
                  style={selectStyle}>
                  <option value="">Select grade…</option>
                  {grades.map(g => <option key={g.id} value={g.id}>{g.display_name} ({g.post_nominal}) — £{g.annual_fee}/yr</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Billing Period</label>
                <select value={form.billing_years} onChange={e => setForm(f => ({ ...f, billing_years: e.target.value }))}
                  style={selectStyle}>
                  <option value="1">1 Year</option>
                  <option value="2">2 Years (5% discount)</option>
                  <option value="3">3 Years (10% discount)</option>
                </select>
              </div>
            </div>

            <div style={{ display: "flex", gap: 24, marginTop: 12 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer" }}>
                <input type="checkbox" checked={form.discount_provider} onChange={e => setForm(f => ({ ...f, discount_provider: e.target.checked }))} />
                Accredited provider discount (10%)
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer" }}>
                <input type="checkbox" checked={form.discount_trainer} onChange={e => setForm(f => ({ ...f, discount_trainer: e.target.checked }))} />
                Trainer/assessor discount (5%)
              </label>
            </div>

            {feeCalc && (
              <div style={{ marginTop: 12, padding: 12, background: colors.background, borderRadius: 8, border: `1px solid ${colors.border}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span style={{ color: colors.textSecondary }}>Base total:</span>
                  <span>£{feeCalc.base_total.toFixed(2)}</span>
                </div>
                {feeCalc.total_discount > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: colors.success }}>
                    <span>Discount:</span>
                    <span>−£{feeCalc.total_discount.toFixed(2)}</span>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15, fontWeight: 700, marginTop: 4, paddingTop: 4, borderTop: `1px solid ${colors.border}` }}>
                  <span>Total:</span>
                  <span style={{ color: colors.primary }}>£{feeCalc.final_fee.toFixed(2)}</span>
                </div>
              </div>
            )}

            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Personal Statement</label>
              <textarea rows={3} value={form.personal_statement} onChange={e => setForm(f => ({ ...f, personal_statement: e.target.value }))}
                placeholder="Applicant's professional statement and reasons for application…"
                style={textareaStyle} />
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, marginTop: 12, cursor: "pointer" }}>
              <input type="checkbox" checked={form.coi_declared} onChange={e => setForm(f => ({ ...f, coi_declared: e.target.checked }))} />
              Conflict of Interest declaration completed
            </label>
          </form>
        </StandardModal>
      )}

      {/* ---- CONFIRM PAYMENT MODAL ---- */}
      {payModal && (
        <StandardModal
          title={`Confirm Payment — ${payModal.invoice_number}`}
          onClose={() => setPayModal(null)}
          width={440}
          footer={
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => setPayModal(null)}>Cancel</Btn>
              <Btn variant="primary" type="submit" form="mark-paid-form" disabled={paySaving}>
                {paySaving ? "Confirming…" : "Confirm Payment"}
              </Btn>
            </div>
          }
        >
          <form id="mark-paid-form" onSubmit={submitMarkPaid}>
            <p style={{ fontSize: 13, color: colors.textSecondary, marginTop: 0 }}>
              Invoice amount: <strong>£{payModal.amount.toFixed(2)}</strong>. Check the Tide account for this
              payment before confirming — this cannot be undone from here.
            </p>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Amount Received (£) *</label>
              <input required type="number" step="0.01" min="0.01" value={payForm.paid_amount}
                onChange={e => setPayForm(f => ({ ...f, paid_amount: e.target.value, confirm_mismatch: false }))}
                style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Bank Reference *</label>
              <input required value={payForm.payment_reference}
                placeholder="Exactly as shown on the Tide statement"
                onChange={e => setPayForm(f => ({ ...f, payment_reference: e.target.value }))}
                style={inputStyle} />
            </div>
            {payForm.confirm_mismatch && (
              <div style={{ marginTop: 12, padding: 10, background: "#fef3c7", borderRadius: 6, fontSize: 12.5, color: "#92400e" }}>
                The amount doesn't match the invoice. Submitting again will record it anyway as a confirmed mismatch (e.g. a partial payment).
              </div>
            )}
          </form>
        </StandardModal>
      )}
    </div>
  );
}
